import { onBeforeUnmount } from 'vue';

const FLOW_TIMEOUT = 30000;

const waitForIframeMessage = ({ url, successTypes, signal, errors }) =>
  new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.hidden = true;
    iframe.src = url;

    const cleanup = () => {
      clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      signal.removeEventListener('abort', onAbort);
      iframe.remove();
    };

    const fail = (message) => {
      cleanup();
      reject(new Error(message));
    };

    const onMessage = (event) => {
      if (event.origin !== window.location.origin || event.source !== iframe.contentWindow) {
        return;
      }

      if (successTypes.includes(event.data?.type)) {
        cleanup();
        resolve(event.data);
      } else if (event.data?.type === 'customer-auth-error') {
        fail(errors.failure);
      }
    };

    const onAbort = () => fail(errors.reset);
    const timeout = setTimeout(() => fail(errors.timeout), FLOW_TIMEOUT);

    window.addEventListener('message', onMessage);
    signal.addEventListener('abort', onAbort, { once: true });
    document.body.appendChild(iframe);
  });

export function useHydraCustomerAuth() {
  let isReady = false;
  let preparationPromise = null;
  let continuationPromise = null;
  let activeAbortController = null;

  const createAbortController = () => {
    activeAbortController?.abort();
    activeAbortController = new AbortController();

    return activeAbortController;
  };

  const releaseAbortController = (abortController) => {
    if (activeAbortController === abortController) {
      activeAbortController = null;
    }
  };

  const prepare = () => {
    if (isReady) {
      return Promise.resolve();
    }

    if (preparationPromise) {
      return preparationPromise;
    }

    isReady = false;

    const abortController = createAbortController();
    const url = new URL(window.route('customer.auth.start'), window.location.origin);
    url.searchParams.set('return_url', window.location.href);

    preparationPromise = waitForIframeMessage({
      url: url.toString(),
      successTypes: ['customer-auth-ready', 'customer-auth-success'],
      signal: abortController.signal,
      errors: {
        timeout: 'Customer auth preparation timeout',
        failure: 'Customer auth preparation failed',
        reset: 'Customer auth preparation reset',
      },
    })
      .then((data) => {
        isReady = data.type === 'customer-auth-ready';

        return data;
      })
      .finally(() => {
        preparationPromise = null;
        releaseAbortController(abortController);
      });

    return preparationPromise;
  };

  const continueAuthentication = (continueUrl) => {
    if (continuationPromise) {
      return continuationPromise;
    }

    isReady = false;

    const abortController = createAbortController();

    continuationPromise = waitForIframeMessage({
      url: continueUrl,
      successTypes: ['customer-auth-success'],
      signal: abortController.signal,
      errors: {
        timeout: 'Customer authentication timeout',
        failure: 'Customer authentication failed',
        reset: 'Customer authentication reset',
      },
    }).finally(() => {
      continuationPromise = null;
      releaseAbortController(abortController);
    });

    return continuationPromise;
  };

  const reset = () => {
    isReady = false;
    activeAbortController?.abort();
    activeAbortController = null;
  };

  onBeforeUnmount(reset);

  return {
    prepare,
    continueAuthentication,
    reset,
  };
}
